import React, { useEffect, useState } from 'react';
import useVariantAvailability from './use-variant-availability';
import { useLocation } from '@reach/router';
import getQueryParams from '~/utils/get-query-params';

type Args = {
  variants: Queries.ProductDetailsFragmentFragment['variants'];
  desiredColorRange?: [number, number];
  shouldFetch?: boolean;
};

/**
 * Provide list of variants, isLoggedIn, desiredColorRange (lowest value, and
 * highest value in range)
 *
 * Will return an available variant or null if none exists
 * If a color query parameter exists in the URL, it will prioritize that variant
 */
const useGetFirstAvailableVariantOrNull = ({ variants, desiredColorRange = [0, 0], shouldFetch = true }: Args) => {
  const [variantIndex, setVariantIndex] = useState(desiredColorRange[0]);
  const [availableVariant, setAvailableVariant] = useState<Queries.VariantDetailsFragmentFragment | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { search } = useLocation();

  const queryParams = getQueryParams(search);
  const colorQuery = typeof queryParams.color === 'string' ? queryParams?.color?.replace('/', '')?.toLowerCase() : '';

  // Find variant index matching the color parameter
  const colorVariantIndex = React.useMemo(() => {
    if (!colorQuery || !variants) return -1;
    return variants.findIndex((variant) => variant?.optionValues?.find((ov) => ov?.slug?.includes(colorQuery)));
  }, [colorQuery, variants]);

  // If color parameter exists and matches a variant, use that index
  useEffect(() => {
    if (colorVariantIndex !== -1) {
      setVariantIndex(colorVariantIndex);
    }
  }, [colorVariantIndex]);

  const { data: isAvailable, isLoading: isFetchingAvailability } = useVariantAvailability({
    sku: variants?.[variantIndex]?.sku ?? '',
    shouldFetch: shouldFetch && !!variants?.[variantIndex]?.sku,
  });

  const [desiredRangeChecked, setDesiredRangeChecked] = useState(false);
  const isNotAvailableOrNoPrivilege = !isAvailable && !isFetchingAvailability;
  const isAvailableAndPrivilege = isAvailable && !isFetchingAvailability;

  useEffect(() => {
    // If we have a color parameter and found a matching variant, don't continue checking other variants
    if (colorVariantIndex !== -1) {
      if (isAvailableAndPrivilege && variants?.[variantIndex]) {
        setAvailableVariant(variants?.[variantIndex]);
        setIsLoading(false);
      } else if (!isFetchingAvailability) {
        setIsLoading(false);
      }
      return;
    }

    // Original logic for checking variants when no color parameter exists
    if (desiredRangeChecked) return;
    // If index matches high value of range, return and set index to 0;
    if (variantIndex === desiredColorRange[1] && !isAvailable) {
      setDesiredRangeChecked(true);
      setVariantIndex(0);
    }

    // Only increment index when no availability
    if (isNotAvailableOrNoPrivilege && variants && variantIndex < variants.length - 1) {
      setVariantIndex((state) => state + 1);
    }
    if (isAvailableAndPrivilege && variants?.[variantIndex]) {
      setAvailableVariant(variants?.[variantIndex]);
      setIsLoading(false);
    }
  }, [isAvailable, isFetchingAvailability, variantIndex, colorVariantIndex]);

  useEffect(() => {
    if (colorVariantIndex !== -1) return; // Skip if we're using a color parameter
    if (!desiredRangeChecked) return;

    // Only increment index when no availability
    if (isNotAvailableOrNoPrivilege && variants && variantIndex < variants.length - 1) {
      setVariantIndex((state) => state + 1);
    }
    // Only update available variant when available
    if (isAvailableAndPrivilege && variants?.[variantIndex]) {
      setAvailableVariant(variants?.[variantIndex]);
      setIsLoading(false);
    } else if (!isFetchingAvailability && variants && variantIndex === variants.length - 1) {
      setIsLoading(false);
    }
  }, [isAvailable, isFetchingAvailability, variantIndex, desiredRangeChecked, colorVariantIndex]);

  return { availableVariant, isLoading };
};

export default useGetFirstAvailableVariantOrNull;
